import React, { useEffect, useRef, useState } from 'react';
import { TerminalTabloElementSettings } from '../../../types/Admin/TerminalAppearanceTypes';
import styles from './WeatherWidget.module.scss';

const GEOCODING_URL = 'https://geocoding-api.open-meteo.com/v1/search';
const WEATHER_URL = 'https://api.open-meteo.com/v1/forecast';
const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 минут
const GEOCODING_TIMEOUT_MS = 10 * 1000; // 10 сек — при отсутствии ответа виджет не показываем

type GeocodingResult = {
  results?: Array<{
    id: number;
    name: string;
    latitude: number;
    longitude: number;
    timezone: string;
  }>;
};

type WeatherResult = {
  current?: {
    temperature_2m: number;
    weather_code: number;
  };
};

const WEATHER_ICON_BASE = 'https://open-meteo.com/images/weather';

/** Возвращает URL иконки погоды по WMO коду. Для неизвестных кодов — иконка "облачно". */
const getWeatherIconUrl = (code: number): string => {
  const knownCodes = [
    0, 1, 2, 3, 45, 48, 51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 71, 73, 75, 77, 80, 81, 82, 85, 86,
    95, 96, 99
  ];
  const iconCode = knownCodes.includes(code) ? code : 3;
  return `${WEATHER_ICON_BASE}/${iconCode}.svg`;
};

const weatherCodeToDescription = (code: number): string => {
  const map: Record<number, string> = {
    0: 'Ясно',
    1: 'Преимущественно ясно',
    2: 'Переменная облачность',
    3: 'Пасмурно',
    45: 'Туман',
    48: 'Изморозь',
    51: 'Морось: слабая',
    53: 'Морось: умеренная',
    55: 'Морось: сильная',
    56: 'Ледяная морось: слабая',
    57: 'Ледяная морось: сильная',
    61: 'Дождь: слабый',
    63: 'Дождь: умеренный',
    65: 'Дождь: сильный',
    66: 'Ледяной дождь: слабый',
    67: 'Ледяной дождь: сильный',
    71: 'Снег: слабый',
    73: 'Снег: умеренный',
    75: 'Снег: сильный',
    77: 'Снежная крупа',
    80: 'Ливень: слабый',
    81: 'Ливень: умеренный',
    82: 'Ливень: сильный',
    85: 'Снежный ливень: слабый',
    86: 'Снежный ливень: сильный',
    95: 'Гроза',
    96: 'Гроза с градом',
    99: 'Гроза с сильным градом'
  };
  return map[code] ?? 'Неизвестно';
};

/** Эмодзи-запас при ошибке загрузки иконки, по категории WMO кода. */
const weatherCodeToEmoji = (code: number): string => {
  if (code === 0) return '☀️';
  if (code >= 1 && code <= 3) return '☁️';
  if (code === 45 || code === 48) return '🌫️';
  if (code >= 51 && code <= 67) return '🌧️';
  if (code >= 71 && code <= 77) return '❄️';
  if (code >= 80 && code <= 82) return '🌦️';
  if (code >= 85 && code <= 86) return '🌨️';
  if (code >= 95 && code <= 99) return '⛈️';
  return '🌡️';
};

type TabloWeatherWidgetProps = {
  element: TerminalTabloElementSettings;
};

export const WeatherWidget = ({ element }: TabloWeatherWidgetProps) => {
  const region = (element.weatherRegion ?? '').trim();
  const [locationName, setLocationName] = useState<string>(region || '—');
  const [temperature, setTemperature] = useState<number | null>(null);
  const [weatherCode, setWeatherCode] = useState<number | null>(null);
  const [description, setDescription] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iconError, setIconError] = useState(false);
  /** При true виджет не отображается (нет ответа от geocoding за 10 сек) */
  const [geocodingUnavailable, setGeocodingUnavailable] = useState(false);
  const geoAbortRef = useRef<AbortController | null>(null);
  const unmountedRef = useRef(false);

  const fetchWeather = React.useCallback(async () => {
    if (!region) {
      setLocationName('—');
      setTemperature(null);
      setDescription('Укажите регион в настройках');
      setLoading(false);
      setError(null);
      setGeocodingUnavailable(false);
      return;
    }

    setError(null);
    setGeocodingUnavailable(false);
    geoAbortRef.current?.abort();
    let geoTimeoutId: ReturnType<typeof setTimeout> | null = null;
    try {
      const geoController = new AbortController();
      geoAbortRef.current = geoController;
      geoTimeoutId = setTimeout(() => geoController.abort(), GEOCODING_TIMEOUT_MS);
      const geoRes = await fetch(
        `${GEOCODING_URL}?name=${encodeURIComponent(region)}&count=1&language=ru`,
        { signal: geoController.signal }
      );
      clearTimeout(geoTimeoutId);
      geoTimeoutId = null;
      geoAbortRef.current = null;
      const geoData: GeocodingResult = await geoRes.json();
      const first = geoData.results?.[0];
      if (!first) {
        setLocationName(region);
        setTemperature(null);
        setDescription('Регион не найден');
        setLoading(false);
        return;
      }

      setLocationName(first.name);

      const weatherRes = await fetch(
        `${WEATHER_URL}?latitude=${first.latitude}&longitude=${first.longitude}&current=temperature_2m,weather_code&timezone=${encodeURIComponent(first.timezone)}`
      );
      const weatherData: WeatherResult = await weatherRes.json();
      if (weatherData.current) {
        setTemperature(Math.round(weatherData.current.temperature_2m));
        setWeatherCode(weatherData.current.weather_code);
        setDescription(weatherCodeToDescription(weatherData.current.weather_code));
        setIconError(false);
      } else {
        setTemperature(null);
        setWeatherCode(null);
        setDescription('Нет данных');
      }
    } catch (e) {
      if (geoTimeoutId) clearTimeout(geoTimeoutId);
      geoAbortRef.current = null;
      const isTimeout = e instanceof Error && e.name === 'AbortError';
      if (isTimeout && !unmountedRef.current) {
        setGeocodingUnavailable(true);
      } else if (!unmountedRef.current) {
        setError('Ошибка загрузки');
      }
      setDescription('');
      setTemperature(null);
      setWeatherCode(null);
    } finally {
      setLoading(false);
    }
  }, [region]);

  useEffect(() => {
    unmountedRef.current = false;
    fetchWeather();
    const id = setInterval(fetchWeather, REFRESH_INTERVAL_MS);
    return () => {
      unmountedRef.current = true;
      clearInterval(id);
      geoAbortRef.current?.abort();
    };
  }, [fetchWeather]);

  const baseTextStyle: React.CSSProperties = {
    fontFamily: element.fontFamily || undefined,
    fontWeight: element.fontWeight || undefined,
    color: element.textColor || undefined
  };

  const regionFontSizePx = Math.min(72, Math.max(8, element.weatherRegionFontSize ?? 14));
  const regionStyle = { fontSize: `${regionFontSizePx}px` };

  if (!region) {
    return (
      <div className={styles.container} style={baseTextStyle}>
        <span className={styles.location} style={regionStyle}>
          📍 Укажите регион
        </span>
      </div>
    );
  }

  if (geocodingUnavailable) {
    return null;
  }

  if (loading && temperature === null && !error) {
    return (
      <div className={styles.container} style={baseTextStyle}>
        <span className={styles.location} style={regionStyle}>
          📍 {locationName}
        </span>
        <span className={styles.loading} style={regionStyle}>
          Загрузка...
        </span>
      </div>
    );
  }

  const showWeatherIcon = weatherCode !== null && !error;
  const iconSizePx = Math.min(200, Math.max(16, element.weatherIconSize ?? 40));
  const textStyle: React.CSSProperties = {
    fontFamily: element.fontFamily || undefined,
    fontWeight: element.fontWeight || undefined,
    color: element.textColor || undefined
  };
  const locationStyle: React.CSSProperties = {
    ...textStyle,
    fontSize: `${regionFontSizePx}px`
  };
  const tempSizePx = Math.min(96, Math.max(14, element.weatherTempFontSize ?? 28));
  const tempStyle: React.CSSProperties = {
    ...textStyle,
    fontSize: `${tempSizePx}px`
  };
  const descSizePx = Math.min(48, Math.max(8, element.weatherDescriptionFontSize ?? 12));
  const descStyle: React.CSSProperties = {
    ...textStyle,
    fontSize: `${descSizePx}px`
  };

  return (
    <div className={styles.container} style={textStyle}>
      <span className={styles.location} style={locationStyle}>
        📍 {locationName}
      </span>
      <div className={styles.tempRow}>
        <span className={styles.temperature} style={tempStyle}>
          {temperature != null ? temperature : '—'}°
        </span>
        {showWeatherIcon &&
          (iconError ? (
            <span
              className={styles.weatherIconFallback}
              style={{ fontSize: iconSizePx }}
              aria-hidden>
              {weatherCodeToEmoji(weatherCode!)}
            </span>
          ) : (
            <img
              className={styles.weatherIcon}
              src={getWeatherIconUrl(weatherCode!)}
              alt=""
              aria-hidden
              style={{ width: iconSizePx, height: iconSizePx }}
              onError={() => setIconError(true)}
            />
          ))}
      </div>
      {description && (
        <span className={styles.description} style={descStyle}>
          {error ? error : description}
        </span>
      )}
    </div>
  );
};
